import React, { useEffect, useState } from 'react'
import * as Styled from './VisibilitySelector.styled'
import { Box, Chip, CircularProgress, MenuItem } from '@material-ui/core'
import SelectField from 'components/Fields/SelectField/SelectField'
import { SupportTier } from 'types'
import { InfoRounded } from '@material-ui/icons'
import { Colors } from 'styles'

type ContentVisibility = 'public' | 'exclusiveToSupporters'
type TierAccess = 'allSupporters' | 'cumulativeTier' | 'fixedTiers'

export interface VisibilityData {
  exclusiveToSupporters: boolean
  minSupportTierId: string | null
  supportTiersIds: string[] | null
}

interface VisibilitySelectorProps {
  tiers: SupportTier[]
  initialValues?: VisibilityData
  isLoading?: boolean
  onChangeData: (data: Partial<VisibilityData>) => void
}

const VisibilitySelector: React.FC<VisibilitySelectorProps> = ({
  tiers,
  onChangeData,
  initialValues,
  isLoading,
}) => {
  const [contentVisibility, setContentVisibility] =
    useState<ContentVisibility>('public')
  const [tierAccess, setTierAccess] = useState<TierAccess>('allSupporters')
  const [baseTierId, setBaseTierId] = useState<string | null>(null)
  const [fixedTiersIds, setFixedTiersIds] = useState<string[]>([])

  const [summaryText, setSummaryText] = useState<string | null>(null)

  useEffect(() => {
    setContentVisibility(
      initialValues?.exclusiveToSupporters ? 'exclusiveToSupporters' : 'public',
    )
    setTierAccess(
      initialValues?.supportTiersIds
        ? 'fixedTiers'
        : initialValues?.minSupportTierId
        ? 'cumulativeTier'
        : 'allSupporters',
    )
    setBaseTierId(initialValues?.minSupportTierId)
    setFixedTiersIds(initialValues?.supportTiersIds ?? [])
  }, [initialValues])

  useEffect(() => {
    if (contentVisibility === 'public') {
      onChangeData({
        exclusiveToSupporters: false,
        minSupportTierId: null,
        supportTiersIds: null,
      })
      setSummaryText(
        'Este conteúdo será público e estará disponível para qualquer usuário.',
      )
      return
    }

    if (tierAccess === 'allSupporters') {
      onChangeData({
        exclusiveToSupporters: true,
        minSupportTierId: null,
        supportTiersIds: null,
      })
      setSummaryText('Este conteúdo será exclusivo para todos os apoiadores.')
      return
    }

    if (tierAccess === 'cumulativeTier') {
      const tier = tiers.find((tier) => tier.id === baseTierId)
      onChangeData(
        !tier
          ? {}
          : {
              exclusiveToSupporters: true,
              minSupportTierId: baseTierId,
              supportTiersIds: null,
            },
      )
      setSummaryText(
        tier
          ? `Este conteúdo será exclusivo para apoiadores a partir do nível R$ ${tier.value} (${tier.title}).`
          : null,
      )
      return
    }

    const tiersLabels = tiers
      .filter((tier) => fixedTiersIds.includes(tier.id))
      .map((tier) => `R$ ${tier.value} (${tier.title})`)
    onChangeData(
      tiersLabels.length === 0
        ? {}
        : {
            exclusiveToSupporters: true,
            minSupportTierId: null,
            supportTiersIds: fixedTiersIds,
          },
    )
    setSummaryText(
      tiersLabels.length > 0
        ? `Este conteúdo será exclusivo para apoiadores dos níveis: ${tiersLabels.join(
            ', ',
          )}.`
        : null,
    )
  }, [
    contentVisibility,
    tierAccess,
    baseTierId,
    fixedTiersIds,
    tiers,
    onChangeData,
  ])

  return (
    <Styled.Container>
      {isLoading && (
        <Styled.LoadingContainer>
          <CircularProgress />
        </Styled.LoadingContainer>
      )}

      <SelectField
        name="contentVisibility"
        label="Selecione a visibilidade"
        variant="outlined"
        displayEmpty
        darkMode
        fullWidth
        value={contentVisibility}
        onChange={(event) => setContentVisibility(event.target.value as any)}
      >
        <MenuItem value="public">Público</MenuItem>
        <MenuItem value="exclusiveToSupporters">
          Exclusivo para apoiadores
        </MenuItem>
      </SelectField>

      {!isLoading && contentVisibility === 'exclusiveToSupporters' && (
        <>
          <SelectField
            name="tierAccess"
            label="Selecione quais níveis de apoio terão acesso"
            variant="outlined"
            displayEmpty
            darkMode
            fullWidth
            value={tierAccess}
            onChange={(event) => setTierAccess(event.target.value as any)}
          >
            <MenuItem value="allSupporters">Todos apoiadores</MenuItem>
            <MenuItem value="cumulativeTier">
              A partir de um nível de apoio
            </MenuItem>
            <MenuItem value="fixedTiers">Níveis de apoio específicos</MenuItem>
          </SelectField>

          {tierAccess === 'cumulativeTier' && (
            <SelectField
              name="baseTier"
              label="Selecione o nível de apoio base"
              variant="outlined"
              displayEmpty
              darkMode
              fullWidth
              value={baseTierId ?? ''}
              onChange={(event) => setBaseTierId(event.target.value as string)}
              error={tiers.length === 0 || !baseTierId}
              disabled={tiers.length === 0}
              helperText={
                tiers.length === 0
                  ? 'Você não possui níveis de apoio para seleção'
                  : !baseTierId
                  ? 'Selecione o nível de apoio base'
                  : undefined
              }
            >
              {tiers.map((tier) => (
                <MenuItem key={tier.id} value={tier.id}>
                  R$ {tier.value} ({tier.title})
                </MenuItem>
              ))}
            </SelectField>
          )}

          {tierAccess === 'fixedTiers' && (
            <SelectField
              name="fixedTiers"
              label="Selecione os níveis de apoio"
              variant="outlined"
              displayEmpty
              darkMode
              fullWidth
              multiple
              value={fixedTiersIds}
              onChange={(event) =>
                setFixedTiersIds(event.target.value as string[])
              }
              renderValue={(selected: string[]) => {
                const values = selected.map((id) => {
                  const tier = tiers.find((tier) => tier.id === id)
                  return tier ? `R$ ${tier.value} (${tier.title})` : null
                })
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gridGap: 5 }}>
                    {values.map((value) => (
                      <Chip key={value} label={value} />
                    ))}
                  </Box>
                )
              }}
              error={tiers.length === 0 || fixedTiersIds.length === 0}
              disabled={tiers.length === 0}
              helperText={
                tiers.length === 0
                  ? 'Você não possui níveis de apoio para seleção'
                  : fixedTiersIds.length === 0
                  ? 'Selecione ao menos um nível de apoio'
                  : undefined
              }
            >
              {tiers.map((tier) => (
                <MenuItem key={tier.id} value={tier.id}>
                  R$ {tier.value} ({tier.title})
                </MenuItem>
              ))}
            </SelectField>
          )}
        </>
      )}

      {summaryText && (
        <Styled.SummaryContainer>
          <InfoRounded fontSize="medium" style={{ color: Colors.TEXT_DARK }} />
          <Styled.SummaryText>{summaryText}</Styled.SummaryText>
        </Styled.SummaryContainer>
      )}
    </Styled.Container>
  )
}

export default VisibilitySelector
